/**
 * Sprachen des Panels: Erkennung, Nachschlagen, Einsetzen.
 *
 * Bewusst ohne Vue und ohne Browser -- damit lässt sich alles mit dem
 * Testläufer unter resources/js/tests prüfen. Die Vue-Anbindung liegt in
 * ./index.js.
 *
 * **Warum kein vue-i18n.** Dessen Nachrichtensyntax deutet `@`, `|`, `{` und
 * `}` als Befehle. Im Panel stehen E-Mail-Adressen, JSON-Beispiele und
 * Platzhalter wie `%CAMPAIGN_URL%` in den Texten; jeder davon hätte
 * maskiert werden müssen, und ein vergessener bricht erst zur Laufzeit.
 * Hier gibt es genau eine Regel: `{name}` wird ersetzt, sonst nichts.
 *
 * @see lang/README.md
 */

export const LOCALES = ['de_de', 'en_gb'];

/** Ohne Wahl und ohne passende Browsersprache gilt Deutsch. */
export const DEFAULT_LOCALE = 'de_de';

/**
 * Die Sprachen, in die App-Editoren übersetzt werden: alle außer Deutsch,
 * denn die Editoren sind auf Deutsch geschrieben.
 */
export const EDITOR_TRANSLATION_LOCALES = LOCALES.filter(value => value !== DEFAULT_LOCALE);

/** Für <html lang>, Intl und Vuetify. */
export const LOCALE_TAGS = {de_de: 'de-DE', en_gb: 'en-GB'};

/** So steht die Sprache im Menü: als Text, nicht als Flagge. */
export const LOCALE_LABELS = {de_de: 'DE', en_gb: 'EN'};

/**
 * Der Name jeder Sprache in ihr selbst -- für Tooltip und Screenreader. Wer
 * die Sprache nicht versteht, in der das Panel gerade steht, soll seine
 * eigene trotzdem erkennen.
 */
export const LOCALE_NAMES = {de_de: 'Deutsch', en_gb: 'English'};

/** Vuetify kennt nur die Sprache, nicht die Region. */
export const VUETIFY_LOCALES = {de_de: 'de', en_gb: 'en'};

export const STORAGE_KEY = 'rc-admin-locale';

/** Mit diesem Header erfährt das Backend die Sprache, siehe SetLocale. */
export const LOCALE_HEADER = 'X-Locale';

/**
 * Eine Sprachangabe auf eine Panel-Sprache abbilden, oder null.
 *
 * Entscheidend ist die Sprache, nicht die Region: `de-AT` und `de-CH` sind
 * Deutsch, `en-US` ist Englisch. Das Panel hat je Sprache genau eine
 * Fassung.
 */
export function normalizeLocale(value) {
    if (typeof value !== 'string' || value === '') return null;

    const exact = value.toLowerCase().replace('-', '_');
    if (LOCALES.includes(exact)) return exact;

    const language = exact.split('_')[0];
    return LOCALES.find(locale => locale.split('_')[0] === language) ?? null;
}

/**
 * Welche Sprache gilt: die gespeicherte Wahl, sonst die erste passende
 * Browsersprache, sonst Deutsch.
 *
 * `languages` ist navigator.languages -- die Reihenfolge, die im System
 * oder im Browser eingestellt ist.
 */
export function detectLocale({stored = null, languages = []} = {}) {
    const chosen = normalizeLocale(stored);
    if (chosen) return chosen;

    for (const language of languages ?? []) {
        const match = normalizeLocale(language);
        if (match) return match;
    }

    return DEFAULT_LOCALE;
}

/**
 * localStorage kann fehlen oder werfen: im privaten Fenster, bei
 * blockierten Websitedaten, in manchen eingebetteten Ansichten. Dann gilt
 * die Browsersprache, und nichts bricht.
 */
export function readStoredLocale(storage) {
    try {
        return normalizeLocale(storage?.getItem(STORAGE_KEY));
    } catch (e) {
        return null;
    }
}

export function writeStoredLocale(storage, locale) {
    if (!storage) return false;

    try {
        storage.setItem(STORAGE_KEY, locale);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Aus dem Ergebnis von import.meta.glob die Nachrichten je Sprache bauen.
 *
 * Pfad `…/lang/de_de/ui/campaigns.json` → messages.de_de.campaigns. Der
 * Dateiname ist der erste Teil jedes Schlüssels.
 */
export function collectMessages(modules) {
    const messages = Object.fromEntries(LOCALES.map(locale => [locale, {}]));

    for (const [path, module] of Object.entries(modules)) {
        const match = path.match(/lang\/([a-z]{2}_[a-z]{2})\/ui\/([\w-]+)\.json$/);
        if (!match || !messages[match[1]]) continue;

        messages[match[1]][match[2]] = module.default ?? module;
    }

    return messages;
}

function lookup(tree, key) {
    let node = tree;

    for (const part of key.split('.')) {
        if (node === null || typeof node !== 'object' || !(part in node)) return undefined;
        node = node[part];
    }

    return node;
}

/**
 * Bei `count` eine Pluralform wählen: Ein Eintrag darf ein Objekt mit
 * `one` und `other` sein (Namen wie Intl.PluralRules), statt einer Syntax
 * im Text.
 */
function pickPlural(value, locale, params) {
    if (value === null || typeof value !== 'object') return value;

    if (typeof params?.count === 'number') {
        const category = new Intl.PluralRules(LOCALE_TAGS[locale]).select(params.count);
        return value[category] ?? value.other;
    }

    return value.other;
}

/** `{name}` ersetzen. Unbekannte Platzhalter bleiben stehen, damit man sie sieht. */
export function interpolate(text, params = {}, transform = String) {
    return text.replace(/\{(\w+)\}/g, (placeholder, name) => {
        return Object.prototype.hasOwnProperty.call(params, name) && params[name] !== undefined && params[name] !== null
            ? transform(params[name])
            : placeholder;
    });
}

export function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Die Übersetzungsfunktion.
 *
 * Fehlt ein Schlüssel in der gewählten Sprache, kommt der deutsche Text --
 * besser Deutsch als ein Schlüssel. Fehlt er auch dort, steht der
 * Schlüssel selbst da und `onMissing` meldet ihn.
 */
export function createTranslator(messages, getLocale, {onMissing = () => {}} = {}) {
    function resolve(key, params) {
        const locale = getLocale();

        for (const candidate of [locale, DEFAULT_LOCALE]) {
            const value = pickPlural(lookup(messages[candidate], key), candidate, params);
            if (typeof value === 'string') {
                if (candidate !== locale) onMissing(key, locale);
                return value;
            }
        }

        onMissing(key, locale);
        return null;
    }

    /** Text mit Platzhaltern, für {{ }} und Attribute. */
    function t(key, params = {}) {
        const text = resolve(key, params);
        return text === null ? key : interpolate(text, params);
    }

    /**
     * Text mit Auszeichnung, nur für v-html. Die Nachricht selbst ist
     * vertrauenswürdig (sie steht in lang/), die Werte nicht -- sie
     * kommen oft aus Nutzereingaben und werden deshalb maskiert.
     */
    function th(key, params = {}) {
        const text = resolve(key, params);
        return text === null ? escapeHtml(key) : interpolate(text, params, escapeHtml);
    }

    function has(key, locale = getLocale()) {
        return typeof pickPlural(lookup(messages[locale], key), locale, {}) === 'string';
    }

    return {t, th, has};
}
